import { FormGroup, FormHelperText, FormLabel, TextField } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { changeUserPassword, ChangeUserPasswordRequest } from "src/apis/user";
import FormSaveAndResetButtons from "src/pages/components/FormSaveAndResetButtons";
import PageContainer from "src/pages/components/PageContainer";
import { getUserId, setHasChangedPassword } from "src/utils/auth";
import { isSuccessOrAllFieldsAreFilled } from "src/utils/form";

const initialValues: ChangeUserPasswordRequest = {
  current_password: '',
  new_password: '',
}

const ChangePassword: FC = (): JSX.Element => {
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<ChangeUserPasswordRequest>(initialValues);

  const isNewPasswordEqualToCurrent = useMemo<boolean>(() => {
    return formValues.new_password !== '' && formValues.new_password === formValues.current_password;
  }, [formValues])

  const isSubmitDisabled = useMemo<boolean>(() => {
    const areAllFieldsFilled = isSuccessOrAllFieldsAreFilled(success, formValues);
    const isNewPasswordValid = formValues.new_password.length >= 8 && !isNewPasswordEqualToCurrent;

    return areAllFieldsFilled || !isNewPasswordValid;
  }, [success, formValues, isNewPasswordEqualToCurrent]);

  const resetFormState = () => {
    setError('');
    setSuccess(false);
    setFormValues(initialValues);
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      await changeUserPassword(getUserId(), formValues);

      setError('');
      setSuccess(true);
      setHasChangedPassword(true);
    } catch (err: any) {
      setError(err?.message);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer title="Alterar senha" error={error}>
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        <FormGroup>
          <FormLabel>Senha atual</FormLabel>
          <TextField
            type="password"
            value={formValues.current_password}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                current_password: e.target.value as string,
              }));
            }}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>Nova senha</FormLabel>
          <TextField
            type="password"
            value={formValues.new_password}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                new_password: e.target.value as string,
              }));
            }}
          />
          {isNewPasswordEqualToCurrent ? (
            <FormHelperText>
              A sua nova senha precisa ter no mínimo 8 caracteres e não pode ser igual a sua senha atual
            </FormHelperText>
          ) : (
            <FormHelperText>
              A sua nova senha precisa ter no mínimo 8 caracteres
            </FormHelperText>
          )}
        </FormGroup>

        <FormSaveAndResetButtons
          isResetFormStateDisabled
          isSubmitDisabled={isSubmitDisabled}
          resetFormState={resetFormState}
          success={success}
          loading={loading}
        />
      </form>
    </PageContainer>
  )
}

export default ChangePassword;
